import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { find } from "lodash";
import { cn } from "@/utils/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { TriangleArrowRight } from "@dreamhub/designsystem/icons";
import Text from "@/components/ui/text";
import Spinner from "@/components/ui/spinner";
import Chip from "@/components/ui/chip";
import SearchInput from "@/components/ui/search-input";
import IconContainer from "@/components/ui/icon-container";
import FilterMenuItem from "@/components/controls/ToolbarActions/ToolbarFilterButton/FilterMenuItem";
import { ObjectEntity } from "@/entities/forms/types";
import { renderObjectEntityIcon } from "@/entities/forms/helpers";
import { SingleSelectOption } from "@/components/controls/SingleSelect";

export interface LinkToSource {
  entity: ObjectEntity;
  label: string;
  options: SingleSelectOption[];
  searchTerm: string;
  onSearch: (term: string) => void;
  isLoading: boolean;
}

interface SubmenuProps {
  source: LinkToSource;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  onSelect: (id: string) => void;
}

const LinkToSubmenu: React.FC<SubmenuProps> = ({
  source,
  isOpen,
  onOpen,
  onClose,
  onSelect
}) => {
  const { t } = useTranslation();
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [searchValue, setSearchValue] = useState(source.searchTerm);

  const handleMouseEnter = () => {
    hoverTimeoutRef.current = setTimeout(onOpen, 100);
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    source.onSearch(value);
  };

  return (
    <Popover open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <PopoverTrigger asChild>
        <FilterMenuItem
          isActive={isOpen}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          dataTest={`link-to-${source.entity}`}
        >
          <div className="flex items-center w-full justify-between gap-2">
            <div className="flex items-center gap-2">
              {renderObjectEntityIcon(source.entity, "sm", "secondary")}
              <Text variant="body">{source.label}</Text>
            </div>
            <IconContainer
              className={cn(
                "transition-opacity duration-300 ease-in-out",
                isOpen ? "opacity-100" : "opacity-0"
              )}
            >
              <TriangleArrowRight />
            </IconContainer>
          </div>
        </FilterMenuItem>
      </PopoverTrigger>
      <PopoverContent
        side="right"
        align="start"
        sideOffset={4}
        padding={4}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="flex flex-col gap-2 min-w-[200px] max-h-[300px]">
          <div className="sticky top-0 bg-surface-elevation2 z-10">
            <SearchInput
              value={searchValue}
              onChange={handleSearchChange}
              placeholder={t("search")}
              hideIcon
            />
          </div>
          <div className="flex flex-col overflow-y-auto min-h-0">
            {source.isLoading ? (
              <div className="flex justify-center py-4">
                <Spinner size="sm" />
              </div>
            ) : source.options.length === 0 ? (
              <Text variant="bodyTertiary" className="px-2 py-4 text-center">
                {t("tables.emptyState.title")}
              </Text>
            ) : (
              source.options.map((option) => (
                <FilterMenuItem
                  key={String(option.id)}
                  layout="compact"
                  onClick={() => onSelect(String(option.id))}
                  dataTest={`link-to-option-${String(option.id)}`}
                >
                  <Text variant="body" className="flex-1 truncate">
                    {option.displayName}
                  </Text>
                </FilterMenuItem>
              ))
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

interface Props {
  sources: LinkToSource[];
  selectedEntity: ObjectEntity | null;
  selectedId: string;
  placeholder: string;
  onSelect: (entity: ObjectEntity, id: string) => void;
  onClear: () => void;
}

const LinkToMenu: React.FC<Props> = ({
  sources,
  selectedEntity,
  selectedId,
  placeholder,
  onSelect,
  onClear
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState<ObjectEntity | null>(null);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) setActiveSubmenu(null);
  };

  const hasSelection = Boolean(selectedEntity && selectedId);
  const selectedSource = sources.find((s) => s.entity === selectedEntity);
  const selectedLabel =
    find(selectedSource?.options, { id: selectedId })?.displayName ?? "";

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Chip
          size="lg"
          data-test="link-to-trigger"
          ghost={!hasSelection}
          filled={hasSelection}
          selected={isOpen}
          icon={renderObjectEntityIcon(
            hasSelection ? selectedEntity : null,
            "sm",
            hasSelection ? undefined : "secondary"
          )}
          label={hasSelection ? selectedLabel || placeholder : placeholder}
          removable={hasSelection}
          onRemove={onClear}
          onClick={() => undefined}
        />
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        padding={4}
        className="flex flex-col gap-1 min-w-44"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {sources.map((source) => (
          <LinkToSubmenu
            key={source.entity}
            source={source}
            isOpen={activeSubmenu === source.entity}
            onOpen={() => setActiveSubmenu(source.entity)}
            onClose={() => setActiveSubmenu(null)}
            onSelect={(id) => {
              onSelect(source.entity, id);
              handleOpenChange(false);
            }}
          />
        ))}
      </PopoverContent>
    </Popover>
  );
};

export default LinkToMenu;
